import React, { useEffect, useState } from 'react';
import useTranslate from '../../../../common/hooks/useTranslate';
import { getHomePage, getHomePageBoards, updatePinnedWidgets } from '../../../services/homeService';
import { getAnalysisContent } from '../../../services/analysisContentService';
import useBoardContentObject from '../../../boards/hooks/useBoardContentObject';
import { moveItem } from '../../../../common/utility/moveItem';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../redux/store';
import { POPUP } from '../../../../redux/types';

import { IWidget } from '../../../boards/types/IWidget';
import { IHomeContent } from '../../../boards/types/IHomeContent';

import ComponentLoader from '../../../ui/loaders/componentLoader/componentLoader';
import CustomisationGuide from '../../../boards/components/customisationGuide/customisationGuide';
import Icon from '../../../ui/icon/icon';
import SummaryWidget from '../../../boards/components/widget/summary/summaryWidget';
import GridWidget from '../../../boards/components/widget/grid/gridWidget';
import DocumentWidget from '../../../boards/components/widget/document/documentWidget';
import TreelistWidget from '../../../boards/components/widget/treelist/treelistWidget';
import ChartWidget from '../../../boards/components/widget/chart/chartWidget';

import '../../../boards/pages/boardsContainer/boardsContainer.scss';
import './widgetPinSection.scss';
import AnnualGridWidget from '../../../boards/components/widget/annualGrid/annualGridWidget';

const WidgetPinSection: React.FC = () => {
  const [widgets, setWidgets] = useState<IWidget[] | IHomeContent[]>([]);
  const [initialWidgets, setInitialWidgets] = useState<IWidget[] | IHomeContent[]>([]);
  const [unpinTrigger, setUnpinTrigger] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [customisation, setCustomisation] = useState<boolean>(false);

  const { buildNodeHierarchy } = useBoardContentObject();

  const { t } = useTranslate();
  const dispatch = useDispatch();

  const impersonate = useSelector((state: RootState) => state.impersonate);

  const handleUnpin = () => {
    setUnpinTrigger(!unpinTrigger);
  };

  const saveCustomisation = () => {
    updatePinnedWidgets(widgets).catch((err) => dispatch({ type: POPUP.SET_SERVER_ERROR, payload: err.message }));
  };

  const handleSetCustomisation = (e) => {
    if (e === true) {
      setInitialWidgets(widgets);
    }
    setCustomisation(e);
  };

  const handleCancel = () => {
    setWidgets(initialWidgets);
  };

  const handleWidgetMove = (widget, position) => {
    let movedWidgets = moveItem(widgets, 'unpinId', widget.unpinId, position);
    widgets.forEach((w) => {
      if (!movedWidgets.find((mw) => mw.id === w.id)) movedWidgets.push(w);
    });
    setWidgets(movedWidgets);
  };

  useEffect(() => {
    getHomePage().then((homePageResult) => {
      setLoading(true);
      return homePageResult;
    }).then((homePageResult) => {
      const widgetsToShow = homePageResult.content;
      getHomePageBoards().then((homePageBoardsResult: any) => {
        let widgetsLocal: IWidget[] = [];
        widgetsToShow.forEach((widgetToShow) => {
          const widgetId = widgetToShow.widgetId;
          const id = widgetToShow.id;
          const contentId = widgetToShow.contentId;
          const widgetTitle = widgetToShow.title;
          const investorObjectId = widgetToShow.investorObjectId;

          const board = homePageBoardsResult.find((item) => {
            return item.id === contentId;
          });

          if (board?.objects) {
            board.objects = board.objects.map((obj) => {
              buildNodeHierarchy(obj.nodes);
              return obj;
            });
          }

          if (board && board.timePeriods?.length > 0) {
            const object = board?.objects?.find((item) => {
              return item.objectPath === widgetToShow.objectPath;
            });

            const widget = board?.widgets?.find((item) => {
              return item.id === widgetId;
            });

            const data: IWidget = {
              scenarios: board.scenarios,
              widget: { ...widget, name: widgetTitle, investorObjectId: investorObjectId },
              setup: widget.setup,
              measures: board.measures,
              periods: board.timePeriods,
              timePeriods: board.timePeriods,
              notes: null,
              boardId: board.id,
              staticProperties: null,
              customisation: null,
              type: widget.type,
              widgets: board.widgets,
              handleWidgetChange: null,
              handleWidgetMove: null,
              changeNewIndex: null,
              changeOldIndex: null,
              boardSelectedCurrency: board.scenarios[0].currencies[0],
              selectedObject: object,
              selectedPeriod: board.timePeriods[board.timePeriods.length - 1],
              investorObjectId: investorObjectId,
              homePage: true,
              unpinId: id,
            };
            widgetsLocal.push(data);
          }
        });
        setWidgets(widgetsLocal);
        setLoading(false);
      }).catch((err) => dispatch({ type: POPUP.SET_SERVER_ERROR, payload: err.message }));
    }).catch((err) => dispatch({ type: POPUP.SET_SERVER_ERROR, payload: err.message }));
  }, [unpinTrigger]);

  return (
    widgets && widgets.length > 0 &&
    <div className='widget-pin-section'>
      <p className='section-title'>{t('HOME.ANALYSIS_PIN_TITLE')}</p>
      {!impersonate.impersonate && <Icon name={'fa-solid fa-gear'} className={'pointer settings-icon'}
                                         onClick={() => handleSetCustomisation(true)} />
      }
      {loading ? (
        <ComponentLoader />
      ) : (
        <div className='boards-grid-container widget-pin-section-container'>
          {widgets && widgets.map((c) => {
            if (c && c.widget && c.widget?.type === 'summary') {
              return (
                <SummaryWidget
                  widget={c.widget}
                  key={`${c.widget.id}${c.selectedObject.objectPath}${c.investorObjectId}`}
                  selectedObject={c.selectedObject}
                  selectedPeriod={c.selectedPeriod}
                  selectedCurrency={c.boardSelectedCurrency}
                  boardData={c}
                  scenarios={c.scenarios}
                  homeContent={c}
                  handleUnpinTrigger={handleUnpin}
                  unpinId={c.unpinId}
                  customisation={customisation}
                  moveWidget={handleWidgetMove}
                />
              );
            }
            if (c && c.widget && c.widget?.type === 'annualGrid') {
              return (
                <AnnualGridWidget
                  widget={c.widget}
                  key={`${c.widget.id}${c.selectedObject.objectPath}${c.investorObjectId}`}
                  selectedObject={c.selectedObject}
                  selectedPeriod={c.selectedPeriod}
                  selectedCurrency={c.boardSelectedCurrency}
                  boardData={c}
                  scenarios={c.scenarios}
                  homeContent={c}
                  handleUnpinTrigger={handleUnpin}
                  unpinId={c.unpinId}
                  customisation={customisation}
                  moveWidget={handleWidgetMove}
                />
              );
            }
            if (c && c.widget && c.widget.type === 'grid') {
              return (
                <GridWidget
                  widget={c.widget}
                  key={`${c.widget.id}${c.selectedObject.objectPath}${c.investorObjectId}`}
                  selectedObject={c.selectedObject}
                  selectedPeriod={c.selectedPeriod}
                  selectedCurrency={c.boardSelectedCurrency}
                  boardData={c}
                  scenarios={c.scenarios}
                  homeContent={c}
                  handleUnpinTrigger={handleUnpin}
                  unpinId={c.unpinId}
                  customisation={customisation}
                  moveWidget={handleWidgetMove}
                />
              );
            }
            if (c && c.widget && c.widget?.type === 'document') {
              return (
                <DocumentWidget
                  widget={c.widget}
                  key={`${c.widget.id}${c.selectedObject.objectPath}${c.investorObjectId}`}
                  selectedObject={c.selectedObject}
                  selectedPeriod={c.selectedPeriod}
                  selectedCurrency={c.boardSelectedCurrency}
                  boardData={c}
                  scenarios={c.scenarios}
                  homeContent={c}
                  handleUnpinTrigger={handleUnpin}
                  unpinId={c.unpinId}
                  customisation={customisation}
                  moveWidget={handleWidgetMove}
                />
              );
            }
            if (c && c.widget && c.widget?.type === 'treelist') {
              return (
                <TreelistWidget
                  widget={c.widget}
                  key={`${c.widget.id}${c.selectedObject.objectPath}${c.investorObjectId}`}
                  selectedObject={c.selectedObject}
                  selectedPeriod={c.selectedPeriod}
                  selectedCurrency={c.boardSelectedCurrency}
                  boardData={c}
                  scenarios={c.scenarios}
                  homeContent={c}
                  handleUnpinTrigger={handleUnpin}
                  unpinId={c.unpinId}
                  customisation={customisation}
                  moveWidget={handleWidgetMove}
                />
              );
            }
            if (c && c.widget && c.widget?.type === 'chart') {
              return (
                <ChartWidget
                  widget={c.widget}
                  key={`${c.widget.id}${c.selectedObject.objectPath}${c.investorObjectId}`}
                  selectedObject={c.selectedObject}
                  selectedPeriod={c.selectedPeriod}
                  selectedCurrency={c.boardSelectedCurrency}
                  boardData={c}
                  scenarios={c.scenarios}
                  homeContent={c}
                  handleUnpinTrigger={handleUnpin}
                  unpinId={c.unpinId}
                  customisation={customisation}
                  moveWidget={handleWidgetMove}
                />
              );
            }
          })}
        </div>
      )}
      <CustomisationGuide customisation={customisation} saveCustomisation={saveCustomisation}
                          handleCustomisation={handleSetCustomisation} handleCancel={handleCancel} noReset={true} />
    </div>
  );
};

export default WidgetPinSection;
